import React, { ChangeEvent } from 'react'
import { useController, useFormContext, UseFormReturn } from 'react-hook-form'
import { TextField as MuiTextField } from '@mui/material'

type TextFieldProps = {
    label: string
    name: string
    validation: {}
    formMethods: UseFormReturn
    onChangeValue: (v: string | null) => void
}

const TextField = ({ label, name, validation = {}, onChangeValue, formMethods, ...props }: TextFieldProps) => {
    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control: formMethods?.control,
        rules: validation,
    })

    // Watch for external changes to the field value
    const watchedValue = formMethods?.watch(name)

    // Use the watched value or the field's current value
    const value = watchedValue !== undefined ? watchedValue : field.value

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value
        field.onChange(newValue === '' ? null : newValue) // Allow setting null for empty input
        if (onChangeValue) onChangeValue(newValue) // Call optional external handler
    }

    const handleBlur = () => {
        field.onBlur()

        void formMethods.trigger(name) // Explicitly trigger validation
    }

    return (
        <MuiTextField
            label={label}
            value={value === null ? '' : value} // Treat null as an empty string
            onChange={handleOnChange}
            onBlur={handleBlur}
            error={!!error}
            helperText={error ? error.message : ''}
            {...props}
        />
    )
}

const RegisteredTextField = (props) => {
    const formMethods = useFormContext()
    const forwardProps = { formMethods, ...props }
    return <TextField {...forwardProps} />
}

export { TextField, RegisteredTextField }
